import { useState, useEffect } from "react";
import { Copy, X, Star, Heart, Sparkle } from "lucide-react";
import fancyTextStyles from "../../components/FancyTextGeneratorToolComponents/fancyTextStyles";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";

export default function FancyTextGenerator() {
    const [input, setInput] = useState("");
    const { darkMode } = useTheme();
    const [fancyTexts, setFancyTexts] = useState([]);
    const [toast, setToast] = useState({ message: "", visible: false });
    const [selectedDecoration, setSelectedDecoration] = useState("none");
    const [decorationIntensity, setDecorationIntensity] = useState(1);
    const [favorites, setFavorites] = useState([]);
    const [activeTab, setActiveTab] = useState("all");
    const [characterCount, setCharacterCount] = useState(0);

    const textDecorations = {
        none: (text) => text,
        underline: (text) => `${text}\n${'═'.repeat(text.length)}`,
        doubleUnderline: (text) => `${text}\n${'═'.repeat(text.length)}\n${'═'.repeat(text.length)}`,
        box: (text) => {
            const horizontal = '─'.repeat(text.length + 2);
            return `┌${horizontal}┐\n│ ${text} │\n└${horizontal}┘`;
        },
        stars: (text) => `★ ${text} ★`,
        hearts: (text) => `❤ ${text} ❤`,
        arrows: (text) => `➤ ${text} ➤`,
        brackets: (text) => `【${text}】`,
        sparkles: (text) => `✨ ${text} ✨`,
        waves: (text) => `〜 ${text} 〜`,
        dashed: (text) => `-- ${text} --`,
        dotted: (text) => `•• ${text} ••`,
        slash: (text) => `// ${text} //`,
    };

    useEffect(() => {
        // Load favorites from localStorage
        const savedFavorites = localStorage.getItem('fancyTextFavorites');
        if (savedFavorites) {
            setFavorites(JSON.parse(savedFavorites));
        }
    }, []);

    useEffect(() => {
        setCharacterCount(input.length);
    }, [input]);

    const showToast = (message) => {
        setToast({ message, visible: true });
        setTimeout(() => {
            setToast({ message: "", visible: false });
        }, 3000);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInput(value);
        generateFancyTexts(value);
    };

    const generateFancyTexts = (text) => {
        if (text) {
            try {
                let generatedTexts = fancyTextStyles.map((style) => style(text));
                
                // Apply selected decoration
                if (selectedDecoration !== "none") {
                    const decorationFn = textDecorations[selectedDecoration];
                    generatedTexts = generatedTexts.map(decorationFn);
                    
                    // Apply intensity (repeat decorations)
                    if (decorationIntensity > 1) {
                        const repeatFn = (t) => {
                            let result = t;
                            for (let i = 1; i < decorationIntensity; i++) {
                                result = decorationFn(result);
                            }
                            return result;
                        };
                        generatedTexts = generatedTexts.map(repeatFn);
                    }
                }
                
                setFancyTexts(generatedTexts);
            } catch (e) {
                setFancyTexts(["Error generating fancy text"]);
            }
        } else {
            setFancyTexts([]);
        }
    };

    const handleDecorationChange = (decoration) => {
        setSelectedDecoration(decoration);
        generateFancyTexts(input);
    };

    const handleIntensityChange = (e) => {
        const value = parseInt(e.target.value);
        setDecorationIntensity(value);
        generateFancyTexts(input);
    };

    const copyToClipboard = (text, message) => {
        navigator.clipboard.writeText(text);
        showToast(message || "Copied to clipboard");
    };

    const clearAll = () => {
        setInput("");
        setFancyTexts([]);
        setSelectedDecoration("none");
        setDecorationIntensity(1);
    };

    const toggleFavorite = (text) => {
        const isFavorite = favorites.some(fav => fav.text === text);
        let updatedFavorites;
        
        if (isFavorite) {
            updatedFavorites = favorites.filter(fav => fav.text !== text);
        } else {
            updatedFavorites = [...favorites, { 
                text, 
                timestamp: new Date().toISOString(),
                style: `Style ${fancyTexts.indexOf(text) + 1}`
            }];
        }
        
        setFavorites(updatedFavorites);
        localStorage.setItem('fancyTextFavorites', JSON.stringify(updatedFavorites));
        showToast(isFavorite ? "Removed from favorites" : "Added to favorites");
    };

    const filteredTexts = activeTab === "favorites" 
        ? fancyTexts.filter(text => favorites.some(fav => fav.text === text))
        : fancyTexts;

    return (
        <>
            <HeaderSection toolId="fancyTextGenerator" />
            <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
                <div className="bg-white rounded-lg dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon icon={faEnvelope} className="mr-3" />
                            Fancy Text Generator
                        </h1>
                        <p className="opacity-90">
                            Generate fancy text in real-time with comprehensive validation checks
                        </p>
                    </div>
                    <div className={`max-w-4xl mx-auto px-4 py-8 ${darkMode ? "bg-gray-800" : "bg-white"}`}>
                        

                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-2">
                                <label className={`block font-medium ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                                    Enter Text
                                </label>
                                <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                    {characterCount}/500 characters
                                </span>
                            </div>
                            <div className="flex justify-between mb-2">
                                <button
                                    onClick={() => copyToClipboard(input)}
                                    className={`text-sm px-3 py-1 rounded-full flex items-center ${
                                        darkMode ? "bg-gray-700 text-white" : "bg-blue-100 text-blue-700"
                                    }`}
                                    disabled={!input}
                                >
                                    <Copy size={14} className="mr-1" />
                                    Copy
                                </button>
                                <button
                                    onClick={clearAll}
                                    className={`text-sm px-3 py-1 rounded-full flex items-center ${
                                        darkMode ? "bg-gray-700 text-white" : "bg-gray-200 text-gray-800"
                                    }`}
                                    disabled={!input && fancyTexts.length === 0}
                                >
                                    <X size={14} className="mr-1" />
                                    Clear
                                </button>
                            </div>
                            <textarea
                                value={input}
                                onChange={handleInputChange}
                                className={`w-full p-3 border rounded-lg ${
                                    darkMode
                                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                        : "bg-gray-50 border-gray-300 text-black placeholder-gray-500"
                                }`}
                                placeholder="Type here..."
                                rows={4}
                                maxLength={500}
                            />
                        </div>

                        {/* Decoration Options Section */}
                        <div className="mb-6">
                            <label className={`block font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                                Text Decoration
                            </label>
                            <div className="flex flex-wrap gap-2 mb-3 overflow-x-auto pb-2 custom-scrollbar">
                                {Object.keys(textDecorations).map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => handleDecorationChange(key)}
                                        className={`px-3 py-1 rounded-full text-sm min-w-max ${
                                            selectedDecoration === key
                                                ? darkMode
                                                    ? "bg-green-600 text-white"
                                                    : "bg-green-500 text-white"
                                                : darkMode
                                                ? "bg-gray-700 text-white"
                                                : "bg-gray-200 text-gray-800"
                                        }`}
                                    >
                                        {key.charAt(0).toUpperCase() + key.slice(1)}
                                    </button>
                                ))}
                            </div>
                            
                            {selectedDecoration !== "none" && (
                                <div className="mt-3">
                                    <label className={`block font-medium mb-2 ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                                        Decoration Intensity: {decorationIntensity}
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="range"
                                            min="1"
                                            max="5"
                                            value={decorationIntensity}
                                            onChange={handleIntensityChange}
                                            className={`flex-1 ${darkMode ? "accent-green-400" : "accent-green-600"}`}
                                        />
                                        <span className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                                            {decorationIntensity}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mb-6">
                            <div className="flex justify-between items-center mb-3">
                                <div className="flex space-x-2">
                                    <h2 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-black"}`}>
                                        Fancy Results
                                    </h2>
                                    <div className="flex border rounded-full overflow-hidden">
                                        <button
                                            onClick={() => setActiveTab("all")}
                                            className={`px-3 py-1 text-sm ${
                                                activeTab === "all"
                                                    ? darkMode
                                                        ? "bg-green-600 text-white"
                                                        : "bg-green-500 text-white"
                                                    : darkMode
                                                    ? "bg-gray-700 text-gray-300"
                                                    : "bg-gray-200 text-gray-700"
                                            }`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => setActiveTab("favorites")}
                                            className={`px-3 py-1 text-sm ${
                                                activeTab === "favorites"
                                                    ? darkMode
                                                        ? "bg-green-600 text-white"
                                                        : "bg-green-500 text-white"
                                                    : darkMode
                                                    ? "bg-gray-700 text-gray-300"
                                                    : "bg-gray-200 text-gray-700"
                                            }`}
                                        >
                                            Favorites
                                        </button>
                                    </div>
                                </div>
                                {filteredTexts.length > 0 && (
                                    <button
                                        onClick={() => copyToClipboard(filteredTexts.join("\n"), "All fancy texts copied")}
                                        className={`text-sm px-3 py-1 rounded-full flex items-center ${
                                            darkMode ? "bg-gray-700 text-white" : "bg-green-100 text-green-700"
                                        }`}
                                    >
                                        <Copy size={14} className="mr-1" />
                                        Copy All
                                    </button>
                                )}
                            </div>
                            
                            {filteredTexts.length > 0 ? (
                                <div className="space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
                                    {filteredTexts.map((text, index) => {
                                        const isFavorite = favorites.some(fav => fav.text === text);
                                        return (
                                            <div
                                                key={index}
                                                className={`p-3 rounded-lg border ${
                                                    darkMode ? "bg-gray-700 border-gray-600" : "bg-gray-100 border-gray-300"
                                                }`}
                                            >
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                                        Style {index + 1}
                                                    </span>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => toggleFavorite(text)}
                                                            className={`p-1 rounded-full ${isFavorite ? "text-yellow-500" : darkMode ? "text-gray-400" : "text-gray-500"}`}
                                                        >
                                                            {isFavorite ? <Star size={16} fill="currentColor" /> : <Star size={16} />}
                                                        </button>
                                                        <button
                                                            onClick={() => copyToClipboard(text)}
                                                            className={`p-1 rounded-full ${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-black"}`}
                                                        >
                                                            <Copy size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className={`text-lg break-words whitespace-pre-line ${darkMode ? "text-white" : "text-black"}`}>
                                                    {text}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className={`text-center py-8 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                                    {activeTab === "favorites" 
                                        ? "No favorites yet. Star styles to save them!"
                                        : "Fancy text will appear here"}
                                </div>
                            )}
                        </div>

                        {toast.visible && (
                            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-black text-white px-4 py-2 rounded-lg z-50 flex items-center gap-2">
                                {toast.message.includes("favorites") ? (
                                    <Star size={16} fill="currentColor" className="text-yellow-400" />
                                ) : (
                                    <Copy size={16} />
                                )}
                                {toast.message}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <BottomSection toolId="fancyTextGenerator" />

            <style jsx global>{`
                .custom-scrollbar::-webkit-scrollbar {
                    height: 6px;
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: ${darkMode ? '#374151' : '#f3f4f6'};
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: ${darkMode ? '#4ade80' : '#10b981'};
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: ${darkMode ? '#22c55e' : '#059669'};
                }
            `}</style>
        </>
    );
}